import { useEffect } from "react";
import { Cookie } from "lucide-react";
import { Nav } from "@/components/site/Nav";
import { Footer } from "@/components/site/Footer";
import { Overline } from "@/components/site/Primitives";
import { openCookiePrefs } from "@/components/site/CookieBanner";
import { useLang } from "@/lib/i18n";
import { privacy, UPDATED } from "@/lib/privacy";
import { fmtDate } from "@/lib/posts";
import { useSeo } from "@/lib/seo";

const P = ({ children }) => <p className="mt-4 text-stone-300 leading-relaxed">{children}</p>;

const Table = ({ head, rows }) => (
  <div className="mt-6 overflow-x-auto rounded-[16px] border border-[var(--line)]" data-testid="privacy-cookie-table">
    <table className="w-full text-sm min-w-[560px]">
      <thead><tr className="text-left font-mono text-[11px] uppercase tracking-[0.15em] text-stone-400 border-b border-[var(--line)]">
        {head.map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}
      </tr></thead>
      <tbody>{rows.map((r) => (
        <tr key={r[0]} className="border-b border-[var(--line)] last:border-0">
          {r.map((c, i) => <td key={i} className={`px-4 py-3 ${i === 0 ? "font-mono text-[var(--gold)]" : "text-stone-300"}`}>{c}</td>)}
        </tr>
      ))}</tbody>
    </table>
  </div>
);

const Section = ({ s, t }) => (
  <section id={s.id} className="scroll-mt-28 pt-12 border-t border-[var(--line)] first:border-0 first:pt-0" data-testid={`privacy-section-${s.id}`}>
    <h2 className="font-display text-3xl md:text-4xl">{s.h}</h2>
    {s.p?.map((x) => <P key={x}>{x}</P>)}
    {s.ul && (
      <ul className="mt-4 space-y-3">
        {s.ul.map((li) => <li key={li} className="flex gap-3 text-stone-300 leading-relaxed"><span className="mt-2.5 w-1.5 h-1.5 rounded-full bg-[var(--gold)] shrink-0" />{li}</li>)}
      </ul>
    )}
    {s.table && <Table head={t.tHead} rows={s.table} />}
    {s.p2?.map((x) => <P key={x}>{x}</P>)}
    {s.id === "cookie" && (
      <button onClick={openCookiePrefs} data-testid="privacy-cookie-prefs-btn" className="btn-gold mt-8 text-sm"><Cookie size={16} /> {t.prefs}</button>
    )}
  </section>
);

export default function PrivacyPage() {
  const { lang } = useLang();
  const t = privacy[lang];
  useSeo({ title: `${t.title} | Lene Mauro`, description: t.intro });
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="site" id="top">
      <Nav />
      <main className="pt-32 md:pt-40 pb-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <Overline>{t.updated} · {fmtDate(UPDATED, lang)}</Overline>
          <h1 className="mt-8 font-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight" data-testid="privacy-title">{t.title}</h1>
          <p className="mt-8 max-w-3xl text-base md:text-lg text-stone-300 leading-relaxed">{t.intro}</p>
          <div className="mt-16 grid lg:grid-cols-12 gap-12">
            <aside className="lg:col-span-3">
              <nav className="lg:sticky lg:top-28" data-testid="privacy-toc">
                <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-stone-400">{t.toc}</p>
                <ul className="mt-4 space-y-2">
                  {t.sections.map((s) => (
                    <li key={s.id}><a href={`#${s.id}`} className="text-sm text-stone-300 hover:text-[var(--gold)] transition-colors">{s.h}</a></li>
                  ))}
                </ul>
              </nav>
            </aside>
            <div className="lg:col-span-8 space-y-12 max-w-3xl min-w-0">
              {t.sections.map((s) => <Section key={s.id} s={s} t={t} />)}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
